import { useEffect, type FC } from 'react';
import { DEFAULT_LOCALE } from './translations';
import { getCurrentLocale, translateText } from './index';

const TEXT_ATTRIBUTES = ['title', 'aria-label', 'aria-description', 'placeholder', 'alt'];
const SKIP_TAGS = new Set(['SCRIPT', 'STYLE', 'TEXTAREA', 'CODE', 'PRE']);
const HAS_KOREAN = /[가-힣]/;
const SKIP_SELECTOR = [
  '[data-no-i18n]',
  '.rtcCustoms_headerTitle',
  '.context_text',
  '.context_time',
  '.rtcNewPopover__chatName',
  '.rtcNewPopover__chatText',
  '.profileBox__name',
  '.rtc_profileEmoji_senderText',
  '.nameBox__text',
].join(',');

const originalTextNodes = new WeakMap<Text, string>();
const originalAttributes = new WeakMap<Element, Map<string, string>>();

function keepOuterWhitespace(source: string, translated: string): string {
  const leading = source.match(/^\s*/)?.[0] ?? '';
  const trailing = source.match(/\s*$/)?.[0] ?? '';
  return `${leading}${translated}${trailing}`;
}

function translatePreservingWhitespace(source: string): string {
  const trimmed = source.trim();
  if (!trimmed || !HAS_KOREAN.test(trimmed)) return source;

  const translated = translateText(trimmed);
  if (translated === trimmed) return source;

  return keepOuterWhitespace(source, translated);
}

function translateTextNode(node: Text): void {
  if (node.parentElement?.closest(SKIP_SELECTOR)) return;

  const current = node.nodeValue ?? '';
  const previousOriginal = originalTextNodes.get(node);
  const previousTranslated = previousOriginal ? translatePreservingWhitespace(previousOriginal) : null;
  const source = previousOriginal && current === previousTranslated ? previousOriginal : current;

  if (!HAS_KOREAN.test(source)) return;

  originalTextNodes.set(node, source);
  const translated = translatePreservingWhitespace(source);
  if (current !== translated) {
    node.nodeValue = translated;
  }
}

function translateAttributes(element: Element): void {
  if (element.closest(SKIP_SELECTOR)) return;

  let originals = originalAttributes.get(element);

  for (const attr of TEXT_ATTRIBUTES) {
    const current = element.getAttribute(attr);
    if (!current || !HAS_KOREAN.test(current)) continue;

    if (!originals) {
      originals = new Map<string, string>();
      originalAttributes.set(element, originals);
    }

    const previousOriginal = originals.get(attr);
    const previousTranslated = previousOriginal ? translatePreservingWhitespace(previousOriginal) : null;
    const source = previousOriginal && current === previousTranslated ? previousOriginal : current;

    originals.set(attr, source);
    const translated = translatePreservingWhitespace(source);
    if (current !== translated) {
      element.setAttribute(attr, translated);
    }
  }
}

function walk(root: Node): void {
  if (root.nodeType === Node.TEXT_NODE) {
    translateTextNode(root as Text);
    return;
  }

  if (root.nodeType !== Node.ELEMENT_NODE && root.nodeType !== Node.DOCUMENT_FRAGMENT_NODE) return;

  const element = root.nodeType === Node.ELEMENT_NODE ? (root as Element) : null;
  if (element) {
    if (SKIP_TAGS.has(element.tagName)) return;
    translateAttributes(element);
  }

  root.childNodes.forEach(walk);
}

const I18nDomTranslator: FC = () => {
  useEffect(() => {
    if (getCurrentLocale() === DEFAULT_LOCALE || typeof document === 'undefined') return;

    const translateDocument = () => walk(document.body);
    translateDocument();

    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'characterData') {
          translateTextNode(mutation.target as Text);
          continue;
        }

        if (mutation.type === 'attributes') {
          translateAttributes(mutation.target as Element);
          continue;
        }

        mutation.addedNodes.forEach(walk);
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
      attributeFilter: TEXT_ATTRIBUTES,
    });

    return () => observer.disconnect();
  }, []);

  return null;
};

export default I18nDomTranslator;
